/**
 * Çevrimdışı yakalama kuyruğu — saf çekirdek.
 *
 * Neden hazır bir eşitleme motoru (ElectricSQL / PowerSync) DEĞİL:
 *
 * O motorlar "veritabanının bir alt kümesini cihazda tut, iki yönlü eşitle"
 * problemini çözer. Bizim problemimiz o değil. Müşteri sinyalsiz bir yerde
 * defteri okuyup düzenlemiyor; faturayı FOTOĞRAFLIYOR. Yani akış tek yönlü:
 * cihazda birikir, bağlantı gelince sunucuya gider. Sunucu tek doğru kaynak
 * olarak kalır.
 *
 * Bu ayrım muhasebede kritik: iki yönlü eşitleme çakışma üretir, çakışma da
 * otomatik birleştirme gerektirir. Mali kayıtta otomatik birleştirme kabul
 * edilemez. Tek yönlü kuyruk bu sorunu çözmez — ortadan kaldırır.
 *
 * Maliyet tarafı da aynı yöne işaret ediyordu: iki motor da ayrı bir servis,
 * Postgres çoğaltma yuvası, aynalanmış şema ve tarayıcıda WASM SQLite ister.
 * Olmayan bir problem için gerçek bir bağımlılık olurdu.
 *
 * İleride "tüm defter çevrimdışı okunabilsin" gerekirse doğru seçim
 * PowerSync'tir (Supabase ile birlikte çalışacak şekilde tasarlanmış).
 * Buradaki sınır o kapıyı açık bırakıyor: kuyruğun dışarıya verdiği söz
 * "gönderilecekler listesi"dir, altındaki teknoloji değil.
 *
 * Bu dosyada tarayıcıya ait hiçbir şey yok — durum makinesi burada, IndexedDB
 * store.ts'te. Bölünme testi mümkün kılmak için: kaybolmayan kayıt iddiası
 * ancak sınanabilirse bir şey ifade eder.
 */

/** Yeniden deneme üst sınırı. Aşılırsa kayıt SİLİNMEZ, kullanıcıya bildirilir. */
export const MAX_ATTEMPTS = 8;
/** Bu yaştan sonra kuyrukta beklemek anlamsız — kullanıcı müdahale etmeli. */
export const GIVE_UP_AFTER_DAYS = 14;
const BASE_DELAY_MS = 5_000;
const MAX_DELAY_MS = 30 * 60_000;
const DAY_MS = 86_400_000;

export type QueueStatus =
  | 'pending'   // gönderilmeyi bekliyor
  | 'sent'      // sunucu aldı, onayladı
  | 'given_up'; // sınır aşıldı; kayıt duruyor, kullanıcı karar verecek

export interface QueueItem {
  /** Eşsiz anahtar. Yeniden denemede DEĞİŞMEZ — sunucu tekrarı bu anahtardan tanır. */
  id: string;
  /** Hangi profilin verisi. Kuyruk bu alanla bölünür; hesaplar birbirine karışmaz. */
  profile_id: string;
  kind: string;
  payload: unknown;
  created_at: string;
  attempts: number;
  next_attempt_at: string;
  status: QueueStatus;
  last_error: string | null;
}

/** Üstel geri çekilme. Sunucu ayaktayken saldırmayalım, ölüyken vazgeçmeyelim. */
export function backoffMs(attempts: number): number {
  const n = Math.max(0, attempts);
  return Math.min(BASE_DELAY_MS * Math.pow(2, n), MAX_DELAY_MS);
}

export function newItem(
  kind: string,
  profileId: string,
  payload: unknown,
  now: Date = new Date(),
  id?: string,
): QueueItem {
  return {
    id: id ?? cryptoId(),
    profile_id: profileId,
    kind,
    payload,
    created_at: now.toISOString(),
    attempts: 0,
    next_attempt_at: now.toISOString(),
    status: 'pending',
    last_error: null,
  };
}

function cryptoId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'q_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Şimdi gönderilebilecekler.
 *
 * profileId ZORUNLU ve sıkı filtrelenir: aynı cihazda başka bir hesaba
 * geçildiğinde önceki hesabın kuyruğu yeni oturumun kimliğiyle GÖNDERİLMEZ.
 * Bu bir kolaylık değil, izolasyon kuralıdır.
 *
 * Sıra oluşturulma zamanına göre: belgeler çekildikleri sırayla gider.
 */
export function readyToSend(items: QueueItem[], profileId: string, now: Date = new Date()): QueueItem[] {
  const t = now.getTime();
  return items
    .filter((i) => i.profile_id === profileId)
    .filter((i) => i.status === 'pending')
    .filter((i) => new Date(i.next_attempt_at).getTime() <= t)
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

/** Sunucu onayladı. Kayıt ancak BURADA kuyruktan düşer. */
export function onSent(item: QueueItem): QueueItem {
  return { ...item, status: 'sent', last_error: null };
}

/**
 * Gönderim başarısız. Kayıt kaybolmaz; sayaç artar ve sıraya geri döner.
 * Sınırlar aşıldıysa 'given_up' olur — yine silinmez, görünür kalır.
 */
export function onFailed(item: QueueItem, error: string, now: Date = new Date()): QueueItem {
  const attempts = item.attempts + 1;
  const tooOld = now.getTime() - new Date(item.created_at).getTime() > GIVE_UP_AFTER_DAYS * DAY_MS;
  const exhausted = attempts >= MAX_ATTEMPTS;
  return {
    ...item,
    attempts,
    last_error: String(error).slice(0, 300),
    status: exhausted || tooOld ? 'given_up' : 'pending',
    next_attempt_at: new Date(now.getTime() + backoffMs(attempts)).toISOString(),
  };
}

/** Kullanıcıya gösterilecek bekleyen sayısı. */
export function pendingCount(items: QueueItem[], profileId: string): number {
  return items.filter((i) => i.profile_id === profileId && i.status === 'pending').length;
}

/** Müdahale gereken kayıtlar. */
export function givenUp(items: QueueItem[], profileId: string): QueueItem[] {
  return items.filter((i) => i.profile_id === profileId && i.status === 'given_up');
}

/** Onaylanmışlar temizlenebilir; bekleyen veya vazgeçilen ASLA silinmez. */
export function isDiscardable(item: QueueItem): boolean {
  return item.status === 'sent';
}
